import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getCompanyName } from "./payrollReportExcelShared";
import {
  formatInrPdf,
  resolvePulseDisplay,
  statusBadgeLabel,
} from "./payrollDashboardAnalytics";
import {
  aspectsToSections,
  cellValue,
  resolveCompareSummaryRows,
} from "./payrollDashboardCompareExport";

function safeText(s) {
  return String(s ?? "")
    .replace(/₹/g, "Rs ")
    .replace(/[·•—–]/g, "-")
    .replace(/[^\x20-\x7E\n]/g, "");
}

function money(n) {
  if (n == null || n === "") return "-";
  if (typeof n === "string" && Number.isNaN(Number(n))) return safeText(n);
  return formatInrPdf(n);
}

/** Simple bar chart for PDF (no canvas / screenshot dependency). */
function drawPdfBarChart(doc, { x, y, w, h, labels = [], values = [], title = "", color = [15, 118, 110] }) {
  const nums = (values || []).map((v) => Number(v) || 0);
  if (!nums.length || nums.every((v) => v === 0)) {
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(safeText(`${title}: no data`), x, y + 8);
    return y + 14;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);
  doc.text(safeText(title), x, y);
  const chartTop = y + 4;
  const chartH = h - 14;
  const max = Math.max(...nums, 1);
  const n = nums.length;
  const gap = 2;
  const barW = Math.max(4, (w - gap * (n + 1)) / n);
  nums.forEach((v, i) => {
    const bh = (v / max) * (chartH - 8);
    const bx = x + gap + i * (barW + gap);
    const by = chartTop + chartH - bh;
    doc.setFillColor(...color);
    doc.rect(bx, by, barW, Math.max(bh, 0.5), "F");
    doc.setFontSize(5.5);
    doc.setTextColor(30, 41, 59);
    const lbl = money(v);
    doc.text(safeText(lbl), bx + barW / 2, by - 1.2, { align: "center" });
    doc.setTextColor(100, 116, 139);
    const tick = String(labels[i] || "").slice(5) || String(labels[i] || "").slice(-5);
    doc.text(safeText(tick), bx + barW / 2, chartTop + chartH + 4, { align: "center" });
  });
  return chartTop + chartH + 10;
}

function drawPdfPieLegend(doc, { x, y, slices = [], title = "" }) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);
  doc.text(safeText(title), x, y);
  let yy = y + 5;
  const palette = [
    [15, 118, 110],
    [29, 78, 216],
    [5, 150, 105],
    [217, 119, 6],
    [220, 38, 38],
    [124, 58, 237],
  ];
  (slices || []).slice(0, 8).forEach((s, i) => {
    const c = palette[i % palette.length];
    doc.setFillColor(...c);
    doc.rect(x, yy - 2.5, 3.5, 3.5, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(30, 41, 59);
    doc.text(safeText(`${s.label}: ${money(s.value)}`), x + 5, yy);
    yy += 5;
  });
  return yy + 2;
}

/**
 * PDF export matching UI pulse / tabs — Helvetica-safe (no rupee glyph).
 */
export function exportPayrollDashboardPdf({
  analytics = null,
  readiness = null,
  regimeFallback = null,
  periodLabel = "",
  pulse: pulseProp = null,
  compareAspects = [],
} = {}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const company = safeText(getCompanyName());
  const label = safeText(periodLabel || analytics?.periodLabel || "-");
  const pulse = pulseProp || resolvePulseDisplay(analytics, readiness);
  const totals = pulse.totals || analytics?.totals || {};

  doc.setFillColor(15, 118, 110);
  doc.rect(0, 0, 297, 18, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(`${company} - Payroll Analytics Dashboard`, 14, 11);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(
    safeText(
      `${label} | ${statusBadgeLabel(analytics?.status)} | ${new Date().toLocaleString("en-IN")}`
    ),
    14,
    16
  );

  doc.setTextColor(30, 41, 59);
  let y = 26;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Company pulse (same as UI)", 14, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [["Metric", "Amount / Count", "Source"]],
    body: [
      ["Headcount", String(pulse.headcount ?? "-"), pulse.headcountSource || "-"],
      [
        "Payroll cost",
        pulse.totalCost == null ? "-" : money(pulse.totalCost),
        pulse.processed ? "Register" : "Not processed",
      ],
      [
        "Cash outflow",
        pulse.totalCashFlow == null ? "-" : money(pulse.totalCashFlow),
        pulse.processed ? "Register" : "Not processed",
      ],
      [
        "Bank / Net pay",
        pulse.netPay == null ? "-" : money(pulse.netPay),
        pulse.processed ? "Register" : "Not processed",
      ],
      ["Fixed cost", money(pulse.fixedCost), pulse.fixedSource || "-"],
      ["Avg fixed / emp", money(pulse.avgFixed), pulse.fixedSource || "-"],
      [
        "Avg earned / emp",
        pulse.avgEarnedCost == null ? "-" : money(pulse.avgEarnedCost),
        pulse.processed ? "Register" : "Not processed",
      ],
      ["Gross pay", pulse.grossPay == null ? "-" : money(pulse.grossPay), "-"],
      ["Statutory liabilities", money(totals.statutoryLiabilities), "-"],
      ["TDS", money(totals.tds), "-"],
      ["PF employer", money(totals.pfEmployer), "-"],
      ["ESI employer", money(totals.esiEmployer), "-"],
      ["Professional tax", money(totals.professionalTax), "-"],
    ],
    styles: { fontSize: 7, cellPadding: 1.5, font: "helvetica" },
    headStyles: { fillColor: [15, 118, 110], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [240, 253, 250] },
    margin: { left: 14, right: 150 },
    tableWidth: 120,
  });

  const taxY = y;
  const tax = analytics?.taxRegime || {};
  autoTable(doc, {
    startY: taxY,
    head: [["Tax and readiness", "Value"]],
    body: [
      ["Old regime", String(tax.oldCount ?? regimeFallback?.oldCount ?? 0)],
      ["New regime", String(tax.newCount ?? regimeFallback?.newCount ?? 0)],
      ["TDS Old", money(tax.tdsOld)],
      ["TDS New", money(tax.tdsNew)],
      ["Listed (scope)", String(readiness?.total ?? "-")],
      ["Ready for run", String(readiness?.ready ?? "-")],
      ["Missing structure", String(readiness?.missingStructure ?? "-")],
      ["Missing CTC", String(readiness?.missingCtc ?? "-")],
      ["Insights pending", String(readiness?.insightStats?.pending ?? "-")],
    ],
    styles: { fontSize: 7, cellPadding: 1.5, font: "helvetica" },
    headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [239, 246, 255] },
    margin: { left: 150, right: 14 },
    tableWidth: 120,
  });

  y = Math.max(doc.lastAutoTable.finalY, taxY) + 8;
  if (y > 155) {
    doc.addPage();
    y = 20;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(30, 41, 59);
  doc.text(`Master-wise (${safeText(analytics?.groupBy || "department")})`, 14, y);

  autoTable(doc, {
    startY: y + 2,
    head: [["Master", "HC", "Fixed", "Earned", "Cash out", "Net", "TDS"]],
    body: (analytics?.masters || []).slice(0, 25).map((r) => [
      safeText(r.label),
      r.headcount,
      money(r.fixedCost),
      money(r.earnedCost),
      money(r.cashOutflow),
      money(r.netPay),
      money(r.tds),
    ]),
    styles: { fontSize: 6.5, cellPadding: 1.2, font: "helvetica" },
    headStyles: { fillColor: [67, 56, 202], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [238, 242, 255] },
    margin: { left: 14, right: 14 },
  });

  y = doc.lastAutoTable.finalY + 8;
  if (y > 160) {
    doc.addPage();
    y = 20;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(
    analytics?.compareActive
      ? "Comparison summary (all selected aspects)"
      : "Trend / comparison series",
    14,
    y
  );

  const summaryRows = resolveCompareSummaryRows(analytics);
  const sections = aspectsToSections(compareAspects);
  let startY = y + 2;

  sections.forEach((section, idx) => {
    if (idx > 0) {
      startY = doc.lastAutoTable.finalY + 8;
    }
    if (startY > 175) {
      doc.addPage();
      startY = 20;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);
    doc.text(safeText(section.title), 14, startY);
    autoTable(doc, {
      startY: startY + 2,
      head: [section.columns.map((c) => safeText(c.label))],
      body: summaryRows.map((row) =>
        section.columns.map((col) => {
          const v = cellValue(row, col);
          if (col.money) return money(v);
          return safeText(v);
        })
      ),
      styles: { fontSize: 6.5, cellPadding: 1.1, font: "helvetica" },
      headStyles: { fillColor: [180, 83, 9], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [255, 251, 235] },
      margin: { left: 14, right: 14 },
    });
  });

  // Variance
  if ((analytics?.variancePie || []).length) {
    y = doc.lastAutoTable.finalY + 8;
    if (y > 170) {
      doc.addPage();
      y = 20;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Cash outflow variance", 14, y);
    autoTable(doc, {
      startY: y + 2,
      head: [["Component", "Amount"]],
      body: (analytics.variancePie || []).map((x) => [safeText(x.label), money(x.value)]),
      styles: { fontSize: 7, font: "helvetica" },
      headStyles: { fillColor: [15, 118, 110], textColor: 255 },
      margin: { left: 14, right: 14 },
      tableWidth: 100,
    });
  }

  // Charts page (drawn from same series as UI)
  doc.addPage();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(30, 41, 59);
  doc.text("Charts (dashboard series)", 14, 16);

  const series =
    analytics?.compareSummary?.length > 0
      ? analytics.compareSummary
      : analytics?.trendSeries || analytics?.series || [];
  const chartLabels = series.map((s) => s.payMonth || s.label);
  const costVals = series.map((s) => s.totalCost || 0);
  const cashVals = series.map((s) => s.totalCashFlow || s.cash || 0);
  const hcVals = series.map((s) => s.headcount || 0);

  drawPdfBarChart(doc, {
    x: 14,
    y: 22,
    w: 130,
    h: 55,
    labels: chartLabels,
    values: costVals,
    title: "Payroll cost by period",
    color: [15, 118, 110],
  });
  drawPdfBarChart(doc, {
    x: 155,
    y: 22,
    w: 130,
    h: 55,
    labels: chartLabels,
    values: cashVals,
    title: "Cash outflow by period",
    color: [5, 150, 105],
  });
  drawPdfBarChart(doc, {
    x: 14,
    y: 90,
    w: 130,
    h: 55,
    labels: chartLabels,
    values: hcVals,
    title: "Headcount by period",
    color: [124, 58, 237],
  });
  drawPdfPieLegend(doc, {
    x: 155,
    y: 90,
    title: "Cash variance mix",
    slices: analytics?.variancePie || [],
  });

  // Attendance chart if present
  const att = analytics?.attendanceCompare || [];
  if (att.length) {
    drawPdfBarChart(doc, {
      x: 155,
      y: 130,
      w: 130,
      h: 50,
      labels: att.map((a) => a.payMonth),
      values: att.map((a) => a.attendanceProcessed || 0),
      title: "Attendance processed",
      color: [29, 78, 216],
    });
  }

  doc.addPage();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(30, 41, 59);
  doc.text("Payroll notifications", 14, 18);
  autoTable(doc, {
    startY: 22,
    head: [["Tone", "Title", "Detail"]],
    body: (analytics?.alerts || []).map((a) => [
      safeText(a.tone),
      safeText(a.title),
      safeText(a.detail),
    ]),
    styles: { fontSize: 7, cellPadding: 1.4, font: "helvetica" },
    headStyles: { fillColor: [185, 28, 28], textColor: 255 },
    margin: { left: 14, right: 14 },
  });

  let y2 = doc.lastAutoTable.finalY + 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("F&F / exits (period)", 14, y2);
  const fnf = analytics?.fnf || {};
  autoTable(doc, {
    startY: y2 + 2,
    head: [["Metric", "Value"]],
    body: [
      ["Count", String(fnf.count || 0)],
      ["Drafts", String(fnf.draftCount || 0)],
      ["Net", money(fnf.netTotal)],
      ["Leave encashment", money(fnf.leaveEncashment)],
      ["Gratuity", money(fnf.gratuity)],
      ["Notice pay", money(fnf.noticePay)],
      ["Notice recovery", money(fnf.noticeRecovery)],
    ],
    styles: { fontSize: 7, font: "helvetica" },
    headStyles: { fillColor: [15, 118, 110], textColor: 255 },
    margin: { left: 14, right: 160 },
    tableWidth: 110,
  });

  const liab = analytics?.liability || {};
  autoTable(doc, {
    startY: y2 + 2,
    head: [["Liability snapshot", "Amount"]],
    body: [
      ["Gratuity provision", money(liab.gratuityProvisionMonthly)],
      ["F&F gratuity", money(liab.fnfGratuity)],
      ["F&F leave encash", money(liab.fnfLeaveEncashment)],
      ["Notice pay", money(liab.fnfNoticePay)],
      ["Notice recovery", money(liab.fnfNoticeRecovery)],
      ["Revision arrears", money(liab.revisionArrears)],
    ],
    styles: { fontSize: 7, font: "helvetica" },
    headStyles: { fillColor: [67, 56, 202], textColor: 255 },
    margin: { left: 150, right: 14 },
    tableWidth: 120,
  });

  // Master compare matrix when compare active
  if ((analytics?.compare || []).some((c) => (c.masters || []).length)) {
    doc.addPage();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.text("Master-wise by compare month", 14, 16);
    const months = (analytics.compare || []).map((c) => c.payMonth);
    const masterMap = new Map();
    (analytics.compare || []).forEach((c) => {
      (c.masters || []).forEach((m) => {
        const key = m.label || "-";
        const row = masterMap.get(key) || { label: key };
        row[`${c.payMonth}_hc`] = m.headcount || 0;
        row[`${c.payMonth}_earned`] = m.earnedCost || 0;
        masterMap.set(key, row);
      });
    });
    const head = ["Master", ...months.flatMap((ym) => [`${ym} HC`, `${ym} Earned`])];
    const body = [...masterMap.values()].slice(0, 30).map((r) => [
      safeText(r.label),
      ...months.flatMap((ym) => [r[`${ym}_hc`] ?? 0, money(r[`${ym}_earned`])]),
    ]);
    autoTable(doc, {
      startY: 20,
      head: [head],
      body,
      styles: { fontSize: 6, font: "helvetica", cellPadding: 1 },
      headStyles: { fillColor: [67, 56, 202], textColor: 255 },
      margin: { left: 14, right: 14 },
    });
  }

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text(
      "PaceHRM - Figures from processed salary register / summary / F&F stores (read-only).",
      14,
      200
    );
    doc.text(`Page ${i} of ${pageCount}`, 280, 200, { align: "right" });
  }

  const safe = String(label).replace(/[^a-zA-Z0-9_]+/g, "_").slice(0, 40);
  const filename = `Payroll_Dashboard_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return filename;
}

/** Single-card PDF (ASCII-safe). */
export function exportPayrollCardPdf(detail) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const company = safeText(getCompanyName());
  doc.setFillColor(15, 118, 110);
  doc.rect(0, 0, 210, 16, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(safeText(`${company} - ${detail?.title || "Card"}`), 14, 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(
    safeText(`${detail?.periodLabel || ""} | ${detail?.status || ""}`),
    14,
    14
  );

  doc.setTextColor(30, 41, 59);
  autoTable(doc, {
    startY: 24,
    head: [["Field", "Value"]],
    body: (detail?.summary || []).map(([k, v]) => [
      safeText(k),
      typeof v === "number" ? money(v) : safeText(v),
    ]),
    styles: { fontSize: 9, font: "helvetica" },
    headStyles: { fillColor: [15, 118, 110], textColor: 255 },
    margin: { left: 14, right: 14 },
  });

  if (detail?.trendRows?.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Month", "Status", "HC", "Fixed", "Cost", "Cash", "Net"]],
      body: detail.trendRows.map((r) => [
        safeText(r.payMonth),
        safeText(r.status),
        r.headcount,
        money(r.fixedCost),
        money(r.totalCost),
        money(r.totalCashFlow),
        money(r.netPay),
      ]),
      styles: { fontSize: 7, font: "helvetica" },
      headStyles: { fillColor: [30, 64, 175], textColor: 255 },
      margin: { left: 14, right: 14 },
    });
  }

  if (detail?.note) {
    const y = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(safeText(detail.note), 14, y, { maxWidth: 180 });
  }

  const filename = `Payroll_Card_${String(detail?.title || "detail")
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .slice(0, 30)}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return filename;
}
